import type { FC } from 'react';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSubmitAnswer } from '../../../bff-api/turn-to/useSubmitAnswer';
import { useSubmitReply } from '../../../bff-api/turn-to/useSubmitReply';
import { Button } from '../../../ui-components';
import { Field } from '../../field';
import { useSubmitAnswerFields } from '../../forms/useSubmitAnswerFields';
import { SubmitQuestionModal } from './SubmitQuestionModal';

export interface AnswerFieldProps {
    id: number;
    onClose: () => void;
    type: 'answer' | 'reply';
    onCloseField?: () => void;
}
export const AnswerField: FC<AnswerFieldProps> = ({ id, onClose, type, onCloseField }) => {
    const intl = useIntl();
    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { answerText: '', firstName: '', lastName: '', email: '', nickName: '' },
    });

    const fields = useSubmitAnswerFields(form);
    const userAnswer = form.watch('answerText');
    const submitQuestion = useSubmitAnswer(id);
    const submitReply = useSubmitReply(id);

    const {
        isOpen: isOpenSubmitAnswer,
        onOpen: onOpenSubmitAnswer,
        onClose: onCloseSubmitAnswer,
    } = useDisclosure();

    const onSubmit = async (): Promise<void> => {
        await form.trigger();
        const errorsLength = Object.keys(form.formState.errors).length;
        if (errorsLength) {
            return;
        }

        await onSubmitAnswer();
    };

    const onSubmitAnswer = async (): Promise<void> => {
        const values = form.getValues();
        const user = {
            emailAddress: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            nickName: values.nickName,
        };
        if (type === 'answer') {
            await submitQuestion.mutateAsync({
                text: userAnswer,
                user,
            });
        } else if (type === 'reply') {
            await submitReply.mutateAsync({
                text: userAnswer,
                user,
            });
        }

        form.reset({});
        onCloseSubmitAnswer();
        onClose();
    };
    return (
        <Box padding={4}>
            <Field {...fields.answerText} />
            <Flex marginTop={6} gap={2}>
                <Button variant="primary-black" onClick={onOpenSubmitAnswer}>
                    <FormattedMessage id="button.submit" defaultMessage="Submit" />
                </Button>
                <Button variant="secondary" onClick={onCloseField}>
                    <FormattedMessage
                        id="form_action_buttons.button.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Flex>
            <SubmitQuestionModal
                id="123"
                isOpen={isOpenSubmitAnswer}
                onClose={onCloseSubmitAnswer}
                fields={fields}
                onSubmit={onSubmit}
                title={intl.formatMessage({
                    id: 'turn-to.submit-modal.who-are-you',
                    defaultMessage: 'Who are you?',
                })}
                headerText={
                    <FormattedMessage
                        id="turn-to.submit-modal.description"
                        defaultMessage="Please enter your name and email address to complete your submission."
                    />
                }
            />
        </Box>
    );
};
